// Auto-derive contextual internal links from a blog post (brand / city / category mentions)
// Used by BlogPost.tsx to feed BlogFilteredSearchLinks with article-relevant search URLs.

import { citiesRO } from "@/data/cities";
import { topCarBrands } from "@/data/carBrands";
import { SEGMENTS } from "@/lib/segmentStats";

export type ContextualLink = {
  title: string;
  description: string;
  url: string;
};

const stripDiacritics = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const wordRegex = (term: string) =>
  new RegExp(`\\b${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");

export function extractContextualLinks(
  title: string,
  content: string,
  category?: string
): ContextualLink[] {
  const haystackRaw = `${title}\n${content}\n${category ?? ""}`;
  const haystack = stripDiacritics(haystackRaw);

  const links: ContextualLink[] = [];
  const seen = new Set<string>();
  const push = (l: ContextualLink) => {
    if (seen.has(l.url)) return;
    seen.add(l.url);
    links.push(l);
  };

  // 1. Brand mentions → /vehicles?make=Brand. Brands named in the title come
  // first, so a BMW article links to BMW before Audi/Mercedes mentioned in passing.
  const titleKey = stripDiacritics(title);
  const brands = [...topCarBrands].sort(
    (a, b) =>
      Number(wordRegex(stripDiacritics(b.name)).test(titleKey)) -
      Number(wordRegex(stripDiacritics(a.name)).test(titleKey)),
  );
  for (const brand of brands) {
    const brandKey = stripDiacritics(brand.name);
    if (wordRegex(brandKey).test(haystack)) {
      push({
        title: `Anunțuri ${brand.name} în România`,
        description: `Vezi anunțurile ${brand.name} de la dealeri și vânzători privați`,
        url: `/vehicles?make=${encodeURIComponent(brand.name)}`,
      });
      const seg = SEGMENTS.find((s) => s.kind === "make" && stripDiacritics(s.value) === brandKey);
      if (seg && wordRegex(brandKey).test(titleKey)) {
        push({
          title: `Statistici ${seg.name}: prețuri și kilometraj`,
          description: `Date reale din anunțurile ${seg.name} active pe RO Auto Market`,
          url: `/statistici/${seg.slug}`,
        });
      }
    }
    if (links.length >= 3) break;
  }

  // 2. City mentions → /masini-de-vanzare-:slug
  for (const city of citiesRO) {
    const cityKey = stripDiacritics(city.name);
    if (cityKey.length < 4) continue;
    if (wordRegex(cityKey).test(haystack)) {
      push({
        title: `Mașini de vânzare în ${city.name}`,
        description: `Anunțuri auto din ${city.name} și împrejurimi`,
        url: `/masini-de-vanzare-${city.slug}`,
      });
    }
    if (links.length >= 4) break;
  }

  // 3. Category-based heuristics
  const cat = stripDiacritics(category ?? "");
  const t = haystack;
  if (/\bsuv\b/.test(t)) {
    push({
      title: "SUV-uri second hand",
      description: "Filtre rapide pentru anunțuri SUV",
      url: "/vehicles?bodyType=suv",
    });
  }
  if (/\bdiesel\b/.test(t)) {
    push({
      title: "Mașini diesel",
      description: "Anunțuri cu motorizare diesel",
      url: "/vehicles?fuelType=diesel",
    });
  }
  if (/\b(electric|hibrid|hybrid)\b/.test(t)) {
    push({
      title: "Mașini electrice și hibride",
      description: "Anunțuri cu motorizare electrică",
      url: "/vehicles?fuelType=electric",
    });
  }
  if (/\bverificat|verificare|incredere|trust\b/.test(t) || /verific/.test(cat)) {
    push({
      title: "Anunțuri auto",
      description: "Oferte de la dealeri și vânzători privați",
      url: "/vehicles",
    });
  }

  return links.slice(0, 4);
}
